import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, Megaphone, Calendar, Flag, Plus, Trash2, Pencil, BadgeCheck, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Spinner, EmptyState } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-nx flex items-center gap-4 p-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]"><Icon className="h-5 w-5 text-white" /></div>
      <div>
        <p className="text-2xl font-bold text-[#0B192C] font-head">{value}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </div>
    </div>
  );
}

const EMPTY_EVENT = { title: "", description: "", date: "", location: "", organiser: "Vishwakarma Nexus", event_type: "", contact_info: "" };
const EMPTY_BIZ = { name: "", industry: "", city: "", service_area: "", description: "", products: "", website: "", contact_email: "", contact_phone: "", representative_name: "", owner_email: "" };

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));
  useEffect(() => {
    loadStats();
    api.get("/meta").then(({ data }) => setEventTypes(data.event_types)).catch(() => {});
  }, []);

  if (!stats) return <div className="container-nx section-nx"><Spinner /></div>;

  return (
    <div className="container-nx section-nx">
      <div className="label-eyebrow mb-2">Admin Panel</div>
      <h1 className="heading-2">Platform moderation</h1>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard icon={Users} label="Members" value={stats.total_members} />
        <StatCard icon={Briefcase} label="Businesses" value={stats.total_businesses} />
        <StatCard icon={Megaphone} label="Opportunities" value={stats.total_opportunities} />
        <StatCard icon={Calendar} label="Events" value={stats.total_events} />
        <StatCard icon={Flag} label="Open reports" value={stats.open_reports} />
      </div>

      <Tabs defaultValue="members" className="mt-10">
        <TabsList className="flex-wrap">
          <TabsTrigger value="members" data-testid="admin-tab-members">Members</TabsTrigger>
          <TabsTrigger value="businesses" data-testid="admin-tab-businesses">Businesses</TabsTrigger>
          <TabsTrigger value="claims" data-testid="admin-tab-claims">
            Claims{stats.pending_claims > 0 ? ` (${stats.pending_claims})` : ""}
          </TabsTrigger>
          <TabsTrigger value="opportunities" data-testid="admin-tab-opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6"><MembersTab onChange={loadStats} /></TabsContent>
        <TabsContent value="businesses" className="mt-6"><BusinessesTab onChange={loadStats} /></TabsContent>
        <TabsContent value="claims" className="mt-6"><ClaimsTab onChange={loadStats} /></TabsContent>
        <TabsContent value="opportunities" className="mt-6"><OppsTab onChange={loadStats} /></TabsContent>
        <TabsContent value="events" className="mt-6"><EventsTab eventTypes={eventTypes} onChange={loadStats} /></TabsContent>
        <TabsContent value="reports" className="mt-6"><ReportsTab onChange={loadStats} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MembersTab({ onChange }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/members").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/members/${id}/status`, null, { params: { status } }); toast.success(`Member ${status}.`); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <Spinner />;
  if (data.items.length === 0) return <EmptyState title="No members yet" icon={Users} />;
  return (
    <div className="space-y-3">
      {data.items.map((m) => (
        <div key={m.id} className="card-nx flex items-center justify-between p-4" data-testid={`admin-member-${m.id}`}>
          <div>
            <p className="font-semibold text-[#0B192C]">{m.name} {m.is_demo && <span className="demo-badge ml-1">Demo</span>}</p>
            <p className="text-xs text-slate-400">{m.email} · {m.city || "—"}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-xs font-semibold capitalize ${m.status === "suspended" ? "text-red-600" : "text-emerald-600"}`}>{m.status || "active"}</span>
            {m.status === "suspended" ? (
              <button onClick={() => setStatus(m.id, "active")} className="btn-outline-navy px-3 py-1.5 text-xs" data-testid={`activate-member-${m.id}`}>Activate</button>
            ) : (
              <button onClick={() => setStatus(m.id, "suspended")} className="inline-flex items-center rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`suspend-member-${m.id}`}>Suspend</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function BusinessesTab({ onChange }) {
  const [data, setData] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_BIZ);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/businesses").then(({ data }) => setData(data));
  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setIndustries(data.industries)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/businesses/${id}/status`, null, { params: { status } }); toast.success(`Business ${status}.`); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!form.name.trim() || !form.industry || !form.city.trim()) {
      toast.error("Business name, industry and city are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, products: form.products.split(",").map((s) => s.trim()).filter(Boolean) };
      if (!payload.owner_email.trim()) delete payload.owner_email;
      await api.post("/admin/businesses", payload);
      toast.success("Community business added.");
      setOpen(false); setForm(EMPTY_BIZ); load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!data) return <Spinner />;
  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={() => { setForm(EMPTY_BIZ); setOpen(true); }} className="btn-gold" data-testid="admin-add-business-btn">
          <Plus className="h-4 w-4" /> Add community business
        </button>
      </div>
      {data.items.length === 0 ? (
        <EmptyState title="No businesses yet" message="Add a community business so it appears in the directory. Owners can claim it later." icon={Briefcase}
          action={<button onClick={() => setOpen(true)} className="btn-gold"><Plus className="h-4 w-4" /> Add community business</button>} />
      ) : (
        <div className="space-y-3">
          {data.items.map((b) => (
            <div key={b.id} className="card-nx flex items-center justify-between p-4" data-testid={`admin-business-${b.id}`}>
              <div>
                <p className="font-semibold text-[#0B192C]">
                  {b.name}
                  {b.is_demo && <span className="demo-badge ml-1">Demo</span>}
                  {b.claimed === false && <span className="chip ml-2" data-testid={`unclaimed-${b.id}`}>Unclaimed</span>}
                </p>
                <p className="text-xs text-slate-400">{b.industry} · {b.city} · <span className="capitalize">{b.status}</span>{b.owner_name ? ` · ${b.owner_name}` : ""}</p>
              </div>
              <div className="flex gap-2">
                {b.status !== "active" && <button onClick={() => setStatus(b.id, "active")} className="btn-outline-navy px-3 py-1.5 text-xs">Approve</button>}
                {b.status !== "suspended" && <button onClick={() => setStatus(b.id, "suspended")} className="btn-outline-navy px-3 py-1.5 text-xs">Suspend</button>}
                <button onClick={() => setStatus(b.id, "removed")} className="inline-flex items-center rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`remove-business-${b.id}`}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Add a community business</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            Add a business on behalf of a community member. Leave the owner email blank to create an <strong>unclaimed</strong> listing the owner can claim later, or enter a registered member's email to assign it directly.
          </p>
          <div className="space-y-4">
            <input value={form.name} onChange={set("name")} placeholder="Business name *" className="input-nx" data-testid="admin-biz-name-input" />
            <Select value={form.industry} onValueChange={(v) => setForm((f) => ({ ...f, industry: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="admin-biz-industry-select"><SelectValue placeholder="Industry *" /></SelectTrigger>
              <SelectContent>{industries.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <input value={form.city} onChange={set("city")} placeholder="City *" className="input-nx" data-testid="admin-biz-city-input" />
              <input value={form.service_area} onChange={set("service_area")} placeholder="Service area" className="input-nx" />
            </div>
            <textarea value={form.description} onChange={set("description")} placeholder="Description of products / services" className="input-nx min-h-[80px]" data-testid="admin-biz-description-input" />
            <input value={form.products} onChange={set("products")} placeholder="Products / specialities (comma separated)" className="input-nx" />
            <input value={form.website} onChange={set("website")} placeholder="Website (optional)" className="input-nx" />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.contact_email} onChange={set("contact_email")} placeholder="Business contact email" className="input-nx" />
              <input value={form.contact_phone} onChange={set("contact_phone")} placeholder="Business contact phone" className="input-nx" />
            </div>
            <div className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Ownership</p>
              <input value={form.representative_name} onChange={set("representative_name")} placeholder="Owner / representative name (shown on unclaimed listing)" className="input-nx mb-3" data-testid="admin-biz-rep-input" />
              <input value={form.owner_email} onChange={set("owner_email")} placeholder="Assign to member email (optional)" className="input-nx" data-testid="admin-biz-owner-email-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={create} disabled={saving} className="btn-gold" data-testid="admin-biz-save-btn">
              {saving ? "Adding..." : "Add business"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OppsTab({ onChange }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/opportunities").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/opportunities/${id}/status`, null, { params: { status } }); toast.success(`Opportunity ${status}.`); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <Spinner />;
  if (data.items.length === 0) return <EmptyState title="No opportunities yet" icon={Megaphone} />;
  return (
    <div className="space-y-3">
      {data.items.map((o) => (
        <div key={o.id} className="card-nx flex items-center justify-between p-4" data-testid={`admin-opp-${o.id}`}>
          <div className="min-w-0">
            <p className="truncate font-semibold text-[#0B192C]">{o.title}</p>
            <p className="text-xs text-slate-400">{o.category} · by {o.author_name} · <span className="capitalize">{o.status}</span></p>
          </div>
          <div className="flex gap-2">
            {o.status === "removed"
              ? <button onClick={() => setStatus(o.id, "active")} className="btn-outline-navy px-3 py-1.5 text-xs">Restore</button>
              : <button onClick={() => setStatus(o.id, "removed")} className="inline-flex items-center rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`remove-opp-${o.id}`}>Remove</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function EventsTab({ eventTypes, onChange }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_EVENT); setOpen(true); };
  const openEdit = (e) => { setEditing(e.id); setForm({ ...EMPTY_EVENT, ...e, date: (e.date || "").slice(0, 10) }); setOpen(true); };
  const set = (k) => (ev) => setForm((f) => ({ ...f, [k]: ev.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.date || !form.location.trim() || !form.organiser.trim()) {
      toast.error("Title, description, date, location and organiser are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      ["id", "status", "is_demo", "created_at", "created_by", "registrations"].forEach((k) => delete payload[k]);
      if (editing) { await api.put(`/admin/events/${editing}`, payload); toast.success("Event updated!"); }
      else { await api.post("/admin/events", payload); toast.success("Event created!"); }
      setOpen(false); load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/events/${id}`); toast.success("Event removed."); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <Spinner />;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button onClick={openNew} className="btn-gold" data-testid="admin-add-event-btn"><Plus className="h-4 w-4" /> Create event</button>
      </div>
      {data.items.length === 0 ? (
        <EmptyState title="No events yet" message="Create your first community event." icon={Calendar}
          action={<button onClick={openNew} className="btn-gold"><Plus className="h-4 w-4" /> Create event</button>} />
      ) : (
        <div className="space-y-3">
          {data.items.map((e) => (
            <div key={e.id} className="card-nx flex items-center justify-between p-4" data-testid={`admin-event-${e.id}`}>
              <div>
                <p className="font-semibold text-[#0B192C]">{e.title} {e.is_demo && <span className="demo-badge ml-1">Demo</span>}</p>
                <p className="text-xs text-slate-400">{e.date} · {e.location}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(e)} className="btn-outline-navy px-3 py-1.5 text-xs" data-testid={`edit-event-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(e.id)} className="inline-flex items-center rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`delete-event-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit event" : "Create event"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <input value={form.title} onChange={set("title")} placeholder="Event title *" className="input-nx" data-testid="event-title-input" />
            <Select value={form.event_type} onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}>
              <SelectTrigger className="input-nx h-auto" data-testid="event-type-select"><SelectValue placeholder="Event type" /></SelectTrigger>
              <SelectContent>{eventTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <input type="date" value={form.date} onChange={set("date")} className="input-nx" data-testid="event-date-input" />
            <input value={form.location} onChange={set("location")} placeholder="Location *" className="input-nx" data-testid="event-location-input" />
            <input value={form.organiser} onChange={set("organiser")} placeholder="Organiser *" className="input-nx" data-testid="event-organiser-input" />
            <input value={form.contact_info} onChange={set("contact_info")} placeholder="Registration / contact info" className="input-nx" data-testid="event-contact-input" />
            <textarea value={form.description} onChange={set("description")} placeholder="Description *" className="input-nx min-h-[100px]" data-testid="event-description-input" />
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} className="btn-gold" data-testid="event-save-btn">{saving ? "Saving..." : editing ? "Save changes" : "Create event"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClaimsTab({ onChange }) {
  const [claims, setClaims] = useState(null);
  const load = () => api.get("/admin/claims").then(({ data }) => setClaims(data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.put(`/admin/claims/${id}/${action}`); toast.success(action === "approve" ? "Claim approved." : "Claim rejected."); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!claims) return <Spinner />;
  if (claims.length === 0) return <EmptyState title="No claim requests" message="When members request to claim a community business, their requests appear here for your approval." icon={BadgeCheck} />;
  const badge = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", rejected: "bg-slate-100 text-slate-500 border-slate-200" };
  return (
    <div className="space-y-3">
      {claims.map((c) => (
        <div key={c.id} className="card-nx flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-claim-${c.id}`}>
          <div className="min-w-0">
            <p className="font-semibold text-[#0B192C]">
              <Link to={`/businesses/${c.business_id}`} className="hover:underline">{c.business_name}</Link>
              <span className={`ml-2 rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${badge[c.status]}`}>{c.status}</span>
            </p>
            <p className="mt-1 text-xs text-slate-400">Claimed by {c.user_name} · {c.user_email}</p>
          </div>
          {c.status === "pending" && (
            <div className="flex gap-2">
              <button onClick={() => act(c.id, "approve")} className="btn-gold px-4 py-2 text-xs" data-testid={`approve-claim-${c.id}`}><Check className="h-4 w-4" /> Approve</button>
              <button onClick={() => act(c.id, "reject")} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`reject-claim-${c.id}`}><X className="h-4 w-4" /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReportsTab({ onChange }) {
  const [reports, setReports] = useState(null);
  const load = () => api.get("/admin/reports").then(({ data }) => setReports(data));
  useEffect(() => { load(); }, []);
  const resolve = async (id) => {
    try { await api.put(`/admin/reports/${id}/resolve`); toast.success("Report resolved."); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!reports) return <Spinner />;
  if (reports.length === 0) return <EmptyState title="No reports" message="No content has been reported. All clear!" icon={Flag} />;
  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <div key={r.id} className="card-nx flex items-center justify-between p-4" data-testid={`admin-report-${r.id}`}>
          <div className="min-w-0">
            <p className="font-semibold text-[#0B192C]">
              <span className="chip mr-2 capitalize">{r.target_type}</span>
              {r.reason}
            </p>
            <p className="mt-1 text-xs text-slate-400">Reported by {r.reporter_name} · <span className="capitalize">{r.status}</span></p>
          </div>
          {r.status === "open" && (
            <button onClick={() => resolve(r.id)} className="btn-outline-navy px-3 py-1.5 text-xs" data-testid={`resolve-report-${r.id}`}>Resolve</button>
          )}
        </div>
      ))}
    </div>
  );
}
